/**
 * @class
 * @description Gives event emitting/listening functionalities to its extenders.
 * @exports Emitter
 * @abstract
 */
export default class Emitter {
    constructor() {
        this._listeners = {};
    }
    /**
     * Sets a listener to a given event. Use emit(event) to trigger those events.
     * @param {string} eventName Name of the event.
     * @param {callback} callback Callback of the event. Receives event and data.
     * @return this
     * @public
     */
    on(eventName, callback) {
        if (!this._listeners[eventName]) {
            this._listeners[eventName] = [];
        }
        this._listeners[eventName].push(callback);
        return this;
    }
    /**
     * Removes given callback from the listeners of this object.
     * @param {string} eventName Name of the event.
     * @param {callback} callback Callback of the event.
     * @return this
     * @public
     */
    off(eventName, callback) {
        if (this._listeners[eventName]) {
            if (callback) {
                const index = this._listeners[eventName].indexOf(callback);
                if (index !== -1) {
                    this._listeners[eventName].splice(index, 1);
                }
            }
            else {
                this._listeners[eventName] = [];
            }
        }
        return this;
    }
    /**
     * Emits given event, triggering all the associated callbacks.
     * @param {string} eventName Name of the event.
     * @param {object} data Custom data to be sent to the callbacks.
     * @param {string} customDataName Replaces the name of the data object for custom purposes.
     * @return this
     * @protected
     */
    emit(eventName, data = {}, customDataName) {
        [eventName, '*'].forEach((event) => {
            if (this._listeners[event]) {
                this._listeners[event].forEach((callback) => {
                    const name = customDataName || 'data';
                    callback({
                        type: eventName,
                        [name]: data
                    });
                });
            }
        });
        return this;
    }
}
